import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CgTwilio } from "react-icons/cg";
import {
  FaFilter,
  FaDesktop,
  FaLayerGroup,
  FaPhoneAlt,
  FaSyncAlt,
  FaCheckCircle,
  FaArrowRight,
  FaSearchPlus,
  FaTimes,
  FaStar,
  FaQuoteLeft,
  FaUserCircle,
  FaBolt,
  FaComments,
  FaClock,
  FaBullseye,
  FaTools,
  FaCalendarAlt,
} from "react-icons/fa";

import { CTABanner, StatsRow } from "../../Components/PortfolioBlocks";
import BrandButton from "../../Components/BrandButton";

const ctaAccent = { base: "#DB2777", hover: "#BE185D", dark: "#9D174D", soft: "#FDF2F8", rgb: "157, 23, 77" };
// Accent: #EC4899 -> buttons, icons, badges | #DB2777 -> text on white

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

const services = [
  {
    icon: <FaFilter />,
    title: "GoHighLevel Sales Funnels",
    desc: "Conversion-focused funnels with pipelines, forms, booking calendars, and automated follow-up, built to turn traffic into booked appointments and paying customers.",
  },
  {
    icon: <FaDesktop />,
    title: "GHL Landing Pages",
    desc: "Fast, mobile-ready landing pages designed inside GoHighLevel and connected to your CRM, workflows, and campaigns from the first click.",
  },
  {
    icon: <FaLayerGroup />,
    title: "Agency & Sub-Account Setup",
    desc: "A branded, well-structured agency workspace with independent sub-accounts, snapshots, and backend settings, so onboarding new clients is quick and consistent.",
  },
  {
    icon: <FaPhoneAlt />,
    title: "Phone, Twilio & A2P Compliance",
    desc: "Twilio configuration, SMS and voice workflows, and complete A2P 10DLC brand and campaign approval, so your messages are delivered without carrier filtering.",
  },
];

const caseStudies = [
  {
    label: "Case Study 01",
    title: "Full GoHighLevel Agency Setup",
    subtitle: "Organized, branded, and ready to manage multiple businesses",
    tags: ["GoHighLevel", "CRM Automation", "Marketing Automation"],
    deliverables: [
      "Structured agency dashboard",
      "Independent sub-accounts",
      "Backend settings configured",
    ],
    challenge:
      "The client needed their agency workspace organized, branded, and ready to manage multiple businesses under one system.",
    solution:
      "We structured the agency dashboard, created independent sub-accounts, and configured the backend settings so everything runs smoothly without technical issues.",
    result:
      "A fully structured agency setup with multiple sub-accounts operating independently. Messaging, automation, and phone systems were ready for real use, and onboarding new clients became smoother and faster.",
    images: [
      { src: "/ghl-agency-dashboard.png", caption: "Agency dashboard" },
      { src: "/ghl-agency-snapshots.png", caption: "Account snapshots" },
      { src: "/ghl-agency-subaccounts.png", caption: "Independent sub-accounts" },
    ],
  },
 {
    label: "Case Study 02",
    title: "GHL Phone System, Twilio & A2P Compliance Approval",
    subtitle: "Verified business, approved campaigns, reliable delivery",
    tags: ["Twilio", "A2P 10DLC", "SMS Workflows"],
    deliverables: [
      "GHL and Twilio phone setup",
      "A2P 10DLC brand and campaign approval",
      "Configuration issues resolved",
    ],
    challenge:
      "The client needed their business verified, their SMS workflows activated, and all messaging issues resolved across GoHighLevel and Twilio.",
    solution:
      "We completed the GHL phone system setup with Twilio, finished brand and campaign registration for A2P 10DLC, and fixed the configuration issues between GHL and Twilio.",
    result:
      "A fully compliant, approved SMS system. Workflows now deliver reliably without carrier filtering or A2P errors, and the client launched their campaigns immediately.",
    images: [
      { src: "/ghl-a2p-approved.png", caption: "A2P brand and campaign approved" },
      { src: "/ghl-phone-compliant.png", caption: "Phone number A2P 10DLC compliant" },
    ],
  },
];

const review = {
  src: "/ghl-client-review.png",
  caption: "Original client review",
};

const stats = [
  { value: "", label: "Average A2P approval time" },
  { value: "", label: "Messages delivered" },
  { value: "", label: "Agency accounts set up" },
];

const Eyebrow = ({ children }) => (
  <motion.p
    className="text-center text-sm md:text-base tracking-widest text-slate-600"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    viewport={{ once: true }}
  >
    <span className="border-b border-dotted border-slate-300 ml-2">
      {children}
    </span>
  </motion.p>
);

const SectionHeading = ({ eyebrow, title, text }) => (
  <>
    <Eyebrow>{eyebrow}</Eyebrow>
    <motion.h2
      className="mx-auto mt-4 text-center max-w-4xl font-display text-3xl sm:text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-tight"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay: 0.15 }}
      viewport={{ once: true }}
    >
      {title}
    </motion.h2>
    {text && (
      <motion.p
        className="text-center mx-auto mt-9 max-w-2xl text-lg tracking-tight text-slate-600 sm:mt-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
        viewport={{ once: true }}
      >
        {text}
      </motion.p>
    )}
  </>
);

const CaseStudy = ({ study, index, onOpen }) => {
  const [tab, setTab] = useState(0);
  const current = study.images[tab];

  const steps = [
    { icon: <FaBullseye />, heading: "The Challenge", body: study.challenge },
    { icon: <FaTools />, heading: "What We Did", body: study.solution },
    { icon: <FaCheckCircle />, heading: "The Result", body: study.result, highlight: true },
  ];

  return (
    <motion.article
     className="relative overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-gray-900/5 text-left transition-shadow duration-300 hover:shadow-2xl hover:ring-pink-200"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      viewport={{ once: true, margin: "-60px" }}
    >
      {/* accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#EC4899] via-[#DB2777] to-[#F9A8D4]" />

      <div className="p-6 md:p-10">
        {/* Header */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="font-display text-5xl font-bold leading-none text-[#ec489933]">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="rounded-full bg-[#ec489922] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#DB2777]">
                {study.label}
              </span>
            </div>
            <h3 className="mt-4 font-display text-2xl md:text-3xl font-semibold text-slate-900">
              {study.title}
            </h3>
            <p className="mt-1 text-slate-500">{study.subtitle}</p>
          </div>

          <div className="flex flex-wrap gap-2 lg:max-w-sm lg:justify-end">
            {study.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-pink-300 hover:bg-pink-50 hover:text-[#DB2777]"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Deliverables */}
        <div className="mt-6 rounded-2xl bg-slate-50 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Key deliverables
          </p>
          <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-3">
            {study.deliverables.map((d) => (
             <li key={d} className="flex items-start gap-2 rounded-lg p-1.5 -m-1.5 text-sm font-medium text-slate-800 transition-colors duration-200 hover:bg-white">
                <FaCheckCircle className="mt-0.5 shrink-0 text-[#EC4899]" />
                {d}
              </li>
            ))}
          </ul>
        </div>

        {/* Challenge / Solution / Result */}
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {steps.map((s, i) => (
            <div
              key={s.heading}
             className={`relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                s.highlight
                  ? "bg-gradient-to-br from-[#DB2777] to-[#BE185D] text-white shadow-lg"
                  : "border border-pink-100 bg-[#fdf2f8]"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl text-base ${
                    s.highlight ? "bg-white/20 text-white" : "bg-white text-[#EC4899] shadow-sm"
                  }`}
                >
                  {s.icon}
                </span>
                <span
                  className={`text-xs font-semibold ${
                    s.highlight ? "text-pink-100" : "text-slate-400"
                  }`}
                >
                  Step {i + 1}
                </span>
              </div>
              <h4
                className={`mt-4 text-sm font-semibold uppercase tracking-wide ${
                  s.highlight ? "text-white" : "text-[#DB2777]"
                }`}
              >
                {s.heading}
              </h4>
              <p
                className={`mt-2 leading-relaxed ${
                  s.highlight ? "text-pink-50" : "text-slate-700"
                }`}
              >
                {s.body}
              </p>
            </div>
          ))}
        </div>

        {/* Screenshot viewer */}
        <div className="mt-8 rounded-2xl bg-slate-900/[0.03] p-3 md:p-4 ring-1 ring-gray-900/5">
          {/* tabs */}
          <div className="mb-3 flex flex-wrap gap-2">
            {study.images.map((img, i) => (
              <button
                key={img.src}
                type="button"
                onClick={() => setTab(i)}
                className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  tab === i
                    ? "bg-[#EC4899] text-white shadow"
                    : "bg-white text-slate-600 ring-1 ring-gray-900/10 hover:bg-pink-50 hover:text-[#DB2777]"
                }`}
              >
                {img.caption}
              </button>
            ))}
          </div>

          {/* browser frame */}
          <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/10">
            <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-pink-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-pink-200" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
              <span className="ml-3 truncate text-xs font-medium text-slate-400">
                {current.caption}
              </span>
            </div>

          <button
            type="button"
            onClick={() => onOpen(current)}
            aria-label={`Enlarge: ${current.caption}`}
            className="group relative block w-full cursor-zoom-in bg-white"
          >
            <img
              src={current.src}
              alt={current.caption}
              loading="lazy"
              decoding="async"
              className="block h-auto w-full"
            />
            <span className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-[#DB2777] opacity-0 shadow transition-opacity duration-300 group-hover:opacity-100">
              <FaSearchPlus /> Click to enlarge
            </span>
            {/* preload the other tabs so switching doesn't flicker */}
            <span className="hidden">
              {study.images.map((img) => (
                <img key={img.src} src={img.src} alt="" />
              ))}
            </span>
          </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const GHLIntegration = () => {
  const [active, setActive] = useState(null);

  // Lightbox: close with Esc and lock page scroll while open
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [active]);

  return (
    <section className="text-black">
      {/* ───────── Hero ───────── */}
      <div className="mt-20 font-sans overflow-x-hidden relative">
        <section className="relative z-10 py-16 md:py-24 px-6 md:px-12 lg:px-24">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center text-center lg:text-left">
            <div className="space-y-8">
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
                <div className="inline-flex justify-center items-center px-4 py-2 rounded-full bg-[#ec489922] text-center font-medium mx-auto lg:mx-0">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EC4899] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#EC4899]"></span>
                  </span>
                  <span className="text-[#DB2777]">GoHighLevel Specialist</span>
                </div>
              </motion.div>

             <motion.h1
              className="text-4xl sm:text-4xl md:text-5xl lg:text-5xl font-display font-bold tracking-tight text-gray-900 text-center lg:text-left"
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
                GoHighLevel Sales Funnels,
              <span className="block text-[#EC4899]">Landing Pages &amp; Setup</span>
              </motion.h1>

              <motion.p
              className="text-md md:text-xl text-gray-600 max-w-2xl mt-2 text-center lg:text-left md:text-center"
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
            >
              We build GoHighLevel sales funnels and landing pages, set up and
              organize agency workspaces, and handle Twilio and A2P compliance,
              so your marketing, messaging, and phone systems run in one
              reliable system.
                <span className="ml-2 font-semibold">
                  One partner for setup, automation, and compliance.
                </span>
              </motion.p>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={3}
                className="flex flex-wrap gap-4 justify-center lg:justify-start"
              >
               <BrandButton as={Link} to="/contact" accent={ctaAccent}>
                  Book a GHL Setup Call
                </BrandButton>
              </motion.div>
            </div>

            {/* Hero card */}
            <div className="relative">
             <motion.div
                initial={{ y: 30 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl bg-[#ec489922] border border-gray-100 p-6"
              >
                <div className="bg-white/80 backdrop-blur-sm border border-gray-100 rounded-2xl text-center relative">
                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    custom={4}
                    className="absolute -top-10 p-8 text-5xl -right-20 text-[#EC4899] rounded-2xl bg-[#ec489922] backdrop-blur-sm shadow-lg z-20 hidden sm:block"
                  >
                    <CgTwilio />
                  </motion.div>

                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    custom={6}
                    className="absolute top-64 p-4 text-5xl -left-14 text-[#EC4899] rounded-2xl bg-[#ec489922] backdrop-blur-sm shadow-lg z-20 hidden sm:block"
                  >
                    <FaSyncAlt />
                  </motion.div>

                  <div className="p-6 bg-white rounded-lg shadow-lg max-w-md mx-auto text-left">
                    <div className="text-sm font-medium text-gray-500 mb-2">
                      Setup Overview
                    </div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                      GoHighLevel Account
                    </h2>

                    <div className="space-y-3 mb-5">
                      {[
                        ["Agency Workspace", "Structured"],
                        ["Sub-Accounts", "Configured"],
                        ["Twilio & Phone", "Connected"],
                        ["A2P 10DLC", "Approved"],
                      ].map(([label, status]) => (
                        <div
                          key={label}
                          className="flex items-center justify-between rounded-md px-2 py-1 text-sm border-b border-gray-100 transition-colors duration-200 hover:bg-pink-50"
                        >
                          <span className="text-gray-600">{label}</span>
                          <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
                            <FaCheckCircle className="text-[#EC4899]" />
                            {status}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-4 mb-5">
                     <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                      Project outcome
                    </p>
                    <p className="text-gray-800 text-sm">
                      Messaging, automation, and phone systems ready for real
                      usage, with new clients onboarded faster.
                    </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {["Funnels", "SMS", "Automation"].map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-pink-100 text-[#DB2777] text-xs font-semibold rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

         <StatsRow items={stats} />

      {/* ───────── Services ───────── */}
      <section className="px-6 md:px-12 lg:px-24 pb-8 mt-10 md:pb-14">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Our Services"
            title="Everything You Need on GoHighLevel"
            text="From the first funnel to full phone compliance, we handle the technical setup so you can focus on growing your business."
          />

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {services.map((item, i) => (
              <motion.div
                key={item.title}
                whileHover={{ y: -6 }}
                className="group rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-900/5 text-left transition-shadow duration-300 hover:shadow-2xl hover:ring-pink-300"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: "easeOut", delay: i * 0.06 }}
                viewport={{ once: true }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ec489922] text-xl text-[#EC4899] transition-colors duration-300 group-hover:bg-[#EC4899] group-hover:text-white">
                  {item.icon}
                </div>
                <h3 className="mt-5 text-xl font-semibold text-slate-900">
                  {item.title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Our Work ───────── */}
      <section
        id="our-work"
        className="px-6 md:px-12 lg:px-24 py-14 md:py-20 scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Our Work"
            title="Real GoHighLevel Projects, Delivered"
            text="Two recent projects, from agency workspace setup to full A2P compliance."
          />

         <div className="mt-14 space-y-12">
          {caseStudies.map((study, i) => (
            <CaseStudy key={study.title} study={study} index={i} onOpen={setActive} />
          ))}
        </div>
        </div>
      </section>

      {/* ───────── Client Review ───────── */}
       <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="Client Review"
              title="Feedback From Our Clients"
              text="A review from a recent GoHighLevel setup and integration project."
            />

            <motion.div
              className="mt-12 grid grid-cols-1 overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-gray-900/5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              {/* Left: testimonial */}
              <div className="relative flex flex-col p-8 text-left md:p-12">
                <div className="pointer-events-none absolute -left-16 -top-16 h-52 w-52 rounded-full bg-[#ec489922] blur-3xl" />

                <div className="relative flex h-full flex-col">
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ec489922] text-lg text-[#EC4899]">
                      <FaQuoteLeft />
                    </span>
                    <span className="flex items-center gap-2 rounded-full bg-[#ec489915] px-3 py-1.5 text-sm font-semibold text-[#DB2777]">
                      <span className="flex gap-0.5 text-[#EC4899]">
                        {[...Array(5)].map((_, i) => (
                          <FaStar key={i} />
                        ))}
                      </span>
                      5.0
                    </span>
                  </div>

                  <blockquote className="mt-8 font-display text-2xl font-medium leading-snug text-slate-900 md:text-3xl">
                    “Jawad was responsive and worked quickly. It saved me so much time.”
                  </blockquote>

                  {/* Facts from the review */}
                  <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                      <FaCalendarAlt className="shrink-0 text-[#EC4899]" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400">Timeline</p>
                        <p className="text-sm font-semibold text-slate-800">Jan – Feb 2026</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                      <FaCheckCircle className="shrink-0 text-[#EC4899]" />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400">Service</p>
                        <p className="text-sm font-semibold text-slate-800">GHL Setup &amp; Integration</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto flex items-center gap-3 border-t border-gray-100 pt-6 mt-8">
                    <FaUserCircle className="shrink-0 text-4xl text-slate-300" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Client</p>
                      <p className="text-sm text-slate-500">GoHighLevel Setup &amp; Integration</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: original review as proof */}
              <button
                type="button"
                onClick={() => setActive(review)}
                aria-label="Enlarge original client review"
                className="group flex cursor-zoom-in flex-col justify-center border-t border-pink-100 bg-[#fdf2f8] p-6 text-left md:p-10 lg:border-l lg:border-t-0"
              >
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#DB2777]">
                  Original review
                </p>

                <div className="relative overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/10 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl">
                  <img
                    src={review.src}
                    alt="Original five-star client review"
                    loading="lazy"
                    decoding="async"
                    className="block h-auto w-full"
                  />
                </div>

                <span className="mt-4 inline-flex items-center gap-2 self-start rounded-lg bg-white px-3.5 py-2 text-sm font-medium text-[#DB2777] shadow-sm ring-1 ring-pink-200 transition-colors duration-200 group-hover:bg-pink-50">
                  <FaSearchPlus /> Click to enlarge
                </span>
              </button>
            </motion.div>
          </div>
        </section>
    {/* ───────── Closing CTA ───────── */}
          <CTABanner
            title="Need GoHighLevel set up the right way?"
            text="Tell us what you are building and we will handle the funnels, sub-accounts, Twilio, and A2P approval."
            buttonLabel="Contact Us"
          />

      {/* ───────── Lightbox ───────── */}
     {active && (
        <motion.div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setActive(null)}
          role="dialog"
          aria-modal="true"
          aria-label={active.caption}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg"
          >
            <FaTimes />
          </button>
          <motion.img
            src={active.src}
            alt={active.caption}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl"
          />
        </motion.div>
      )}
    </section>
  );
};